import React, { useState, useMemo } from "react";
import { compareOperationalRecords } from "../lib/sortUtils";
import { Btn, Badge, Field, Input, Select, Textarea, StatCard, Toast, useToast } from "../components/ui";
import { fmtDate, today, createClientId, compressImage } from "../lib/formatters";
import { isCompactViewport, SYSTEM_LISTS_DEFAULTS, T} from "../lib/globals";
import { makeCreateMeta, makeUpdateMeta } from "../lib/meta";
import { makeSeed } from "../lib/seed";
import { getAccounts } from "../lib/authUtils";
import { buildNextRecordNumber } from "../lib/recordNumberUtils";
import { appendProvisionalSuffix, appendUpdatePendingDisplaySuffix, markOperationalCreatePending, markOperationalUpdatePending } from "../lib/operationalProvisional";
import { isAdminNotificationEnabled } from "../lib/adminNotificationPrefs";
import { useSysLists } from "../sync/SyncProvider";
import { LIVE_ACTIVITY_EVENT_TYPES, LIVE_ACTIVITY_MODULES, notifyOperationalActivity } from "../lib/liveActivities";
import { stampRecordGeneration } from "../lib/farmGenerations";

const VET_TYPES     = ["Vacinação","Consulta","Cirurgia","Exame","Vermifugação","Outro"];
const VET_PRIORITIES = [
  { id:"normal", label:"Normal", color:"#3B82F6" },
  { id:"high", label:"Alta", color:"#F59E0B" },
  { id:"urgent", label:"Urgente", color:"#EF4444" },
  { id:"emergency", label:"Emergência", color:"#7C3AED" },
];
function getVetPriorityConfig(id){
  return VET_PRIORITIES.find(p=>p.id===id) || VET_PRIORITIES[0];
}

export function createVetModule(deps) {
  const {
    useFarm,
    useFarmCollection,
    useGlobalCollection,
    normalizeAdminGlobalsConfig,
    canCreateInModule,
    canEditInModule,
    canDeleteInModule,
    useSyncOnSave,
    useBgSyncToast,
    AGE_RANGES,
  } = deps;

  function VetModule({session, isGestor}){
  const seed=makeSeed();
  const SL = useSysLists();
  const { fid: currentFarmId, farm: currentFarm } = useFarm();
  const [records,setRecords]=useFarmCollection("vet",seed.vet);
  const ensureVetRecordNumbers = React.useCallback((items=[])=>{
    const working = Array.isArray(items) ? items.map(item=> item && typeof item === "object" ? { ...item } : item).filter(Boolean) : [];
    working.forEach((record)=>{ if (!record || record.recordNumber) return; Object.assign(record, buildNextRecordNumber(working.filter(Boolean), { moduleKey:"veterinary" })); });
    return working;
  }, []);
  const moduleCardsConfig = (()=>{
    try{
      const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
      return parsed && typeof parsed === "object" ? parsed : {};
    }catch(_){ return {}; }
  })();
  const [animals]=useFarmCollection("animals",seed.animals);
  const [lots]=useFarmCollection("animalLots", []);
  const [pastures]=useFarmCollection("pastures", []);
  const [items,setItems]=useGlobalCollection("stock",seed.stock);
  const [adminGlobalsConfigRows] = useGlobalCollection("adminGlobalsConfig", [normalizeAdminGlobalsConfig(null)]);
  const adminGlobalsConfig = useMemo(()=>normalizeAdminGlobalsConfig((adminGlobalsConfigRows||[])[0]), [adminGlobalsConfigRows]);
  const [, setNotifications] = useFarmCollection("notifications", []);
  const [form,setForm]=useState(null);
  const [selectedRecordId,setSelectedRecordId]=useState("");
  const [vetView,setVetView]=useState("individual");
  const [individualSearch,setIndividualSearch]=useState("");
  const [lotSearch,setLotSearch]=useState("");
  const [vetAgendaView,setVetAgendaView]=useState("month");
  const [vetAgendaMonth,setVetAgendaMonth]=useState(()=>new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [vetAgendaSelectedDate,setVetAgendaSelectedDate]=useState(today());
  const vetInlineTopRef = React.useRef(null);
  const vetInlineRefs = React.useRef(new Map());
  const [toast,show]=useToast();
  const [vetPauseForm,setVetPauseForm]=useState(null);
  const [vetMaterialForm,setVetMaterialForm]=useState(null);
  const [vetPhotoForm,setVetPhotoForm]=useState(null);
  const [vetOpenRecordId, setVetOpenRecordId] = useState("");
  async function buildVetNotificationRecipients(record){
    const accounts = await getAccounts();
    const farmId = String(record?.farmId || currentFarmId || "").trim();
    const recipientsMap = new Map();
    const pushRecipient = (acc, roleOverride="")=>{
      if(!acc) return;
      const accountId = String(acc?.id || "").trim();
      const username = String(acc?.username || "").trim().toLowerCase();
      if(!accountId && !username) return;
      const key = accountId || username;
      if(recipientsMap.has(key)) return;
      recipientsMap.set(key, { accountId, userId:String(acc?.userId || "").trim(), username, role:String(roleOverride || acc?.role || "").trim().toLowerCase(), name:String(acc?.name || "").trim() });
    };
    const canAccessFarm = (acc)=>{ const farmIds = Array.isArray(acc?.farmIds) ? acc.farmIds.filter(Boolean) : []; return !farmId || !farmIds.length || farmIds.includes(farmId); };
    accounts.filter(acc=>String(acc?.role||"").trim().toLowerCase()==="admin" && acc?.active!==false).forEach(acc=>pushRecipient(acc, "admin"));
    accounts.filter(acc=>String(acc?.role||"").trim().toLowerCase()==="gestor" && acc?.active!==false && canAccessFarm(acc)).forEach(acc=>pushRecipient(acc, "gestor"));
    return Array.from(recipientsMap.values());
  }

  function buildVetLiveActivityDisplay(record={}, meta={}){
    const priorityMeta = getVetPriorityConfig(record?.priority || meta?.priority || "normal");
    const stepperLabels = ["Criado", "Agendado", "Atendimento", "Medicação", "Concluído"];
    const statusText = String(record?.status || meta?.status || "Agendado").trim();
    const stepperCurrent = statusText.includes("Concl") || statusText.includes("Realizado") ? "Concluído"
      : statusText.includes("andamento") || statusText.includes("Paus") ? "Atendimento"
      : Array.isArray(record?.products) && record.products.length ? "Medicação"
      : statusText.includes("Agend") ? "Agendado"
      : "Criado";
    return {
      displayTitle: "Trembo - Veterinário",
      displayBadge: priorityMeta?.label || "Normal",
      recordCode: record?.recordNumber || record?.id || "",
      displayDate: fmtDate(record?.date || record?.nextDate || record?.createdAt || record?.updatedAt || ""),
      costCenterName: record?.costCenter || record?.lotSummary || "",
      stepperLabels,
      stepperCurrent,
    };
  }



  function isVetLiveActivityEnabled(){
    const liveSettingsFromAdmin = adminGlobalsConfig?.liveActivitySettings;
    const liveGloballyDisabled = liveSettingsFromAdmin?.enabled === false;
    const liveModuleDisabled = liveSettingsFromAdmin?.modules?.[LIVE_ACTIVITY_MODULES.VETERINARY] === false;
    return !liveGloballyDisabled && !liveModuleDisabled;
  }

  function makeProvisionalVetRecordNumber(baseRecord={}){
    const next = buildNextRecordNumber(records||[], { moduleKey:"veterinary" });
    const recordNumber = appendProvisionalSuffix(baseRecord?.recordNumber || next.recordNumber);
    return {
      ...next,
      recordNumber,
      provisionalRecordNumber: recordNumber,
      recordNumberStatus:"provisional",
      provisional:true,
    };
  }

  function displayVetRecordNumber(record){
    if(!record) return "—";
    const rn = String(record.recordNumber || record.id || "").trim();
    if(!rn) return "—";
    if(record.pendingLocalUpdate && !record.pendingLocalCreate) return appendUpdatePendingDisplaySuffix(rn);
    return rn;
  }

  async function appendVetNotification(record, meta={}){
    try{
      record = stampRecordGeneration(record, currentFarm);
      const eventAt = String(meta?.eventAt || record?.updatedAt || record?.createdAt || new Date().toISOString()).trim() || new Date().toISOString();
      const liveDisplay = buildVetLiveActivityDisplay(record, meta);
      const liveActor = session?.name || record?.vet || record?.createdBy || "Sistema";
      if(isVetLiveActivityEnabled()){
        notifyOperationalActivity({
          module: LIVE_ACTIVITY_MODULES.VETERINARY,
          eventType: String(meta?.event || "").includes("recorded") || String(meta?.event || "").includes("created") ? LIVE_ACTIVITY_EVENT_TYPES.CREATED : LIVE_ACTIVITY_EVENT_TYPES.STATUS_CHANGED,
          record,
          title: liveDisplay.displayTitle || String(meta?.title || "Atendimento veterinário realizado").trim() || "Atendimento veterinário realizado",
          status: meta?.status || record?.status || "Concluído",
          previousStatus: meta?.previousStatus || "",
          farmId: String(record?.farmId || currentFarmId || "").trim(),
          farmName: record?.farmName || currentFarm?.name || "",
          extraFarmShortCode: currentFarm?.shortCode || currentFarm?.sigla || record?.farmShortCode || record?.farmSigla || "",
          updatedAt: eventAt,
          actor: liveActor,
          priority: record?.priority || meta?.priority || "normal",
          ...liveDisplay,
          extra: { event: String(meta?.event || "vet_recorded").trim() || "vet_recorded", eventLabel: String(meta?.eventLabel || "Atendimento realizado").trim() || "Atendimento realizado", recordNumber: record?.recordNumber || "", ...liveDisplay },
        })?.catch?.(() => {});
      }
      if(!isAdminNotificationEnabled(adminGlobalsConfig, "veterinary")) return;
      const recipients = await buildVetNotificationRecipients(record);
      if(!recipients.length) return;
      const summary = String(meta?.summary || meta?.body || "").trim() || `Atendimento veterinário ${record?.recordNumber || record?.id || ""} realizado.`;
      const notifId = createClientId("ntf");
      const notification = {
        id:notifId,
        clientId:notifId,
        farmId:String(record?.farmId || currentFarmId || "").trim(),
        type:"veterinary_event",
        event:String(meta?.event || "vet_recorded").trim() || "vet_recorded",
        eventLabel:String(meta?.eventLabel || "Atendimento realizado").trim() || "Atendimento realizado",
        title:String(meta?.title || "Atendimento veterinário realizado").trim() || "Atendimento veterinário realizado",
        summary,
        body:String(meta?.body || summary).trim() || summary,
        entityType:"veterinary",
        entityId: record?.id || "",
        entityRecordNumber: record?.recordNumber || "",
        orderId: record?.id || "",
        orderRecordNumber: record?.recordNumber || "",
        notificationModuleId:"veterinary",
        notificationSectionKey:"veterinary",
        status: meta?.status || record?.status || "Concluído",
        orderStatus: meta?.status || record?.status || "Concluído",
        costCenter: record?.costCenter || "",
        requester: session?.name || record?.vet || record?.createdBy || "Sistema",
        recipients,
        openedInApp:false,
        readBy:[],
        eventAt,
        ...makeCreateMeta(session?.name || "", { updatedAt:eventAt }),
      };
      setNotifications(prev=>[...(Array.isArray(prev)?prev:[]), notification]);
      syncOnSave("notifications");
    }catch(_e){}
  }
  useBgSyncToast("vet", show);
  React.useEffect(()=>{
    if(!currentFarmId) return;
    try{
      const raw = localStorage.getItem("fz:admin:open-vet-record");
      if(!raw) return;
      const parsed = JSON.parse(raw || "null");
      if(String(parsed?.farmId || "") !== String(currentFarmId || "")) return;
      const targetId = String(parsed?.recordId || "").trim();
      if(!targetId) return;
      setVetOpenRecordId(targetId);
      localStorage.removeItem("fz:admin:open-vet-record");
    }catch(_e){}
  }, [currentFarmId]);

  React.useEffect(()=>{
    if(!vetOpenRecordId) return;
    const match = (records||[]).find(r=>String(r?.id||"")===String(vetOpenRecordId));
    if(!match) return;
    setVetView(match.entryMode === "lot" ? "lot" : "individual");
    setSelectedRecordId(String(match.id || ""));
    setForm(null);
    setVetOpenRecordId("");
  }, [vetOpenRecordId, records]);

  React.useEffect(()=>{
    const normalized = ensureVetRecordNumbers(records||[]);
    if ((normalized||[]).some((item, idx)=>String(item?.recordNumber||"") !== String((records||[])[idx]?.recordNumber||""))) {
      setRecords(normalized);
    }
  }, [ensureVetRecordNumbers, records, setRecords]);
  const syncOnSave = useSyncOnSave();
  const canCreate = canCreateInModule(session,"vet") || canEditInModule(session,"vet");
  const canEdit = canEditInModule(session,"vet");
  const canDelete = canDeleteInModule(session,"vet");
  const getConfiguredSectionCards = React.useCallback((sectionId, fallback=[])=>{
    const moduleConfig = moduleCardsConfig?.vet;
    const aliases = {
      "moduleHome": ["moduleHome"],
      "submenu:atendimentos:agenda": ["submenu:atendimentos:agenda", "submenu:attendance:agenda"],
    };
    const keys = aliases[sectionId] || [sectionId];
    for(const key of keys){
      if(moduleConfig && Object.prototype.hasOwnProperty.call(moduleConfig, key)){
        const configured = moduleConfig?.[key];
        if(Array.isArray(configured)) return configured.map(v=>String(v||""));
        return [];
      }
    }
    return (Array.isArray(fallback) ? fallback : []).map(v=>String(v||""));
  }, [moduleCardsConfig]);
  const hasConfiguredSectionCards = React.useCallback((sectionId)=>{
    const moduleConfig = moduleCardsConfig?.vet;
    const aliases = {
      "moduleHome": ["moduleHome"],
      "submenu:atendimentos:agenda": ["submenu:atendimentos:agenda", "submenu:attendance:agenda"],
    };
    const keys = aliases[sectionId] || [sectionId];
    return !!keys.find(key => moduleConfig && Object.prototype.hasOwnProperty.call(moduleConfig, key));
  }, [moduleCardsConfig]);
  const allowedVetCards = React.useMemo(()=>{
    const direct = getConfiguredSectionCards("moduleHome", []);
    return hasConfiguredSectionCards("moduleHome") ? direct : ["agenda","medicamentos","procedimentos","historico"];
  }, [getConfiguredSectionCards, hasConfiguredSectionCards]);
  const allowedVetAgendaCards = React.useMemo(()=>{
    const direct = getConfiguredSectionCards("submenu:atendimentos:agenda", []);
    return hasConfiguredSectionCards("submenu:atendimentos:agenda") ? direct : ["vet_agendados","vet_realizados","vet_nao_realizados","vet_em_andamento"];
  }, [getConfiguredSectionCards, hasConfiguredSectionCards]);

  const normalizeText = React.useCallback((value)=>String(value||"").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase(), []);
  const pastureMap = React.useMemo(()=>Object.fromEntries((Array.isArray(pastures)?pastures:[]).map(p=>[String(p.id), p.name || p.title || p.label || "Sem localização"])), [pastures]);
  const ageRangeMap = React.useMemo(()=>Object.fromEntries((Array.isArray(AGE_RANGES)?AGE_RANGES:[]).map((range, idx)=>{
    if(range && typeof range === "object") return [String(range.id), range.label || range.id];
    return [String(range), String(range)];
  })), []);
  const lotCurrentQty = React.useCallback((lot)=>{
    const base = Number(lot?.currentQty ?? lot?.quantity ?? 0);
    const mortality = Array.isArray(lot?.mortalityEntries) ? lot.mortalityEntries.reduce((sum, entry)=>sum + Math.max(0, Number(entry?.qty||0)), 0) : 0;
    const transfers = Array.isArray(lot?.transfersOut) ? lot.transfersOut.reduce((sum, entry)=>sum + Math.max(0, Number(entry?.qty||0)), 0) : 0;
    return Math.max(0, base - mortality - transfers);
  }, []);
  const formatLotLabel = React.useCallback((lot)=>{
    if(!lot) return "";
    const qty = lotCurrentQty(lot);
    const location = pastureMap[String(lot?.pastureId||"")] || lot?.pastureName || lot?.location || lot?.where || "Sem localização";
    const age = lot?.ageLabel || ageRangeMap[String(lot?.ageRange || lot?.currentAgeRange || lot?.ageRangeId || "")] || lot?.birdStageLabel || lot?.birdStage || lot?.era || "Faixa não informada";
    return `${lot?.name || lot?.code || "Lote"} · ${qty} animal(is) · ${location} · ${age}`;
  }, [ageRangeMap, lotCurrentQty, pastureMap]);
  const lotOptions = React.useMemo(()=> (Array.isArray(lots) ? lots : [])
    .filter(l=>!(l?._deleted || l?.deletedAt || l?.deleted_at))
    .sort((a,b)=>String(a?.name||a?.code||"").localeCompare(String(b?.name||b?.code||"")))
  , [lots]);

  const normalizedRecords = React.useMemo(()=>(Array.isArray(records) ? records : [])
    .filter(r => !(r?._deleted || r?.deletedAt || r?.deleted_at))
    .map(r=>{
      const animalIds = Array.isArray(r?.animalIds) ? r.animalIds.map(v=>String(v)) : [];
      const animalTags = Array.isArray(r?.animalTags) && r.animalTags.length
        ? r.animalTags.map(v=>String(v||"").trim()).filter(Boolean)
        : (r?.animalTag ? [String(r.animalTag).trim()].filter(Boolean) : []);
      const animalNames = Array.isArray(r?.animalNames) && r.animalNames.length
        ? r.animalNames.map(v=>String(v||"").trim()).filter(Boolean)
        : (r?.animalName ? [String(r.animalName).trim()].filter(Boolean) : []);
      return {
        ...r,
        entryMode: String(r?.entryMode || (Array.isArray(r?.lotIds) && r.lotIds.length ? "lot" : "individual")),
        lotIds: Array.isArray(r?.lotIds) ? r.lotIds.map(v=>String(v)) : [],
        animalIds,
        animalTags,
        animalNames,
        procedures: Array.isArray(r?.procedures) && r.procedures.length ? r.procedures : (r?.type ? [r.type] : []),
        photos: Array.isArray(r?.photos) ? r.photos : [],
        status: String(r?.status || (r?.nextDate ? "Agendado" : "Concluído")),
        priority: r?.priority || "normal",
        workflowHistory: Array.isArray(r?.workflowHistory) ? r.workflowHistory : [],
      };
    })
    .sort(compareOperationalRecords), [records]);

  const vetProducts = React.useMemo(()=> (Array.isArray(items) ? items : []).filter(i=>i.vetUse), [items]);
  const vetTypeOptions = React.useMemo(()=> (SL.VET_TYPES||VET_TYPES), [SL]);
  const vetPauseReasonOptions = React.useMemo(()=> (SL?.VET_PAUSE_REASONS && SL.VET_PAUSE_REASONS.length ? SL.VET_PAUSE_REASONS : SYSTEM_LISTS_DEFAULTS.VET_PAUSE_REASONS || []), [SL]);
  const defForm = React.useMemo(()=>({
    _new:true,
    entryMode:"individual",
    animalTag:"",
    animalName:"",
    animalIds:[],
    animalTags:[],
    animalNames:[],
    lotIds:[],
    type:vetTypeOptions[0]||VET_TYPES[0],
    date:today(),
    vet:"",
    cost:0,
    notes:"",
    nextDate:"",
    procedures:[],
    products:[],
    photos:[],
    status:"Agendado",
    priority:"normal",
    workflowStartedAt:"",
    pausedAt:"",
    pauseReason:"",
    pauseNote:"",
    completedAt:"",
    workflowHistory:[]
  }), [vetTypeOptions]);
// eslint-disable-next-line no-unused-vars
  const _selectedRecord = React.useMemo(()=> normalizedRecords.find(r=>String(r.id)===String(selectedRecordId)) || null, [normalizedRecords, selectedRecordId]);
  const cardGrid = isCompactViewport() ? "1fr" : "repeat(auto-fit,minmax(280px,1fr))";
  const panelStyle = { background:T.card, border:`1px solid ${T.border}`, borderRadius:18, padding:isCompactViewport()?16:20, boxShadow:`0 12px 28px ${T.shadow || "rgba(0,0,0,.08)"}` };
  const weekDays = ["Seg","Ter","Qua","Qui","Sex","Sáb","Dom"];
  function addDays(baseDate, days){ const next=new Date(baseDate); next.setDate(next.getDate()+days); return next; }
  function toDateInputValue(value){ if(!value) return ""; if(value instanceof Date){ const y=value.getFullYear(); const m=String(value.getMonth()+1).padStart(2,"0"); const d=String(value.getDate()).padStart(2,"0"); return `${y}-${m}-${d}`; } const text=String(value).trim(); if(/^\d{4}-\d{2}-\d{2}$/.test(text)) return text; const parsed=new Date(text); return Number.isNaN(parsed.getTime())?"":toDateInputValue(parsed); }
  function monthTitle(date){ try{return date.toLocaleDateString("pt-BR", { month:"long", year:"numeric" });}catch(_){ return ""; } }

  const pickVetType = React.useCallback((preferred, fallback)=>{
    const normalizedOptions = vetTypeOptions.map(opt=>({ value:opt, key:String(opt||"").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() }));
    const target = String(preferred||"").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const found = normalizedOptions.find(opt=>opt.key.includes(target) || target.includes(opt.key));
    return found?.value || fallback || vetTypeOptions[0] || VET_TYPES[0];
  }, [vetTypeOptions]);

  const filteredIndividualRecords = React.useMemo(()=> normalizedRecords.filter(record=>{
    if(record.entryMode !== "individual") return false;
    if(!individualSearch.trim()) return true;
    const haystack = [record.animalName, record.animalTag, ...(record.animalNames||[]), ...(record.animalTags||[]), record.type, record.vet, record.notes].map(normalizeText).join(" ");
    return haystack.includes(normalizeText(individualSearch));
  }), [normalizedRecords, individualSearch, normalizeText]);

  const filteredLotRecords = React.useMemo(()=> normalizedRecords.filter(record=>{
    if(record.entryMode !== "lot") return false;
    if(!lotSearch.trim()) return true;
    const lotLabels = (record.lotIds||[]).map(id=>formatLotLabel(lotOptions.find(l=>String(l.id)===String(id)))).join(" ");
    const haystack = [record.type, record.vet, record.notes, lotLabels].map(normalizeText).join(" ");
    return haystack.includes(normalizeText(lotSearch));
  }), [normalizedRecords, lotSearch, normalizeText, formatLotLabel, lotOptions]);

  React.useEffect(()=>{
    if(!form) return;
    const target = form._new ? vetInlineTopRef.current : vetInlineRefs.current.get(String(form.id||selectedRecordId||""));
    if(target && typeof target.scrollIntoView === "function") setTimeout(()=>target.scrollIntoView({ behavior:"smooth", block:"start" }), 40);
  }, [form, selectedRecordId]);

  React.useEffect(()=>{
    const openIndividuals = ()=>{ setVetView("individual"); setForm(null); setSelectedRecordId(""); };
    const openLots = ()=>{ setVetView("lot"); setForm(null); setSelectedRecordId(""); };
    const openAgenda = ()=>{ setVetView("agenda"); setForm(null); setSelectedRecordId(""); };
    const openModuleRoot = ()=>{ setForm(null); setSelectedRecordId(""); };
    window.addEventListener("fz:vet:view-individual", openIndividuals);
    window.addEventListener("fz:vet:view-lot", openLots);
    window.addEventListener("fz:vet:view-agenda", openAgenda);
    window.addEventListener("fz:app:navigate-back-to-module-start", openModuleRoot);
    return ()=>{
      window.removeEventListener("fz:vet:view-individual", openIndividuals);
      window.removeEventListener("fz:vet:view-lot", openLots);
      window.removeEventListener("fz:vet:view-agenda", openAgenda);
      window.removeEventListener("fz:app:navigate-back-to-module-start", openModuleRoot);
    };
  }, []);

  function addProduct(){
    if(!vetProducts.length) return;
    setForm(f=>({...f,products:[...(f.products||[]),{stockId:vetProducts[0].id,name:vetProducts[0].name,unit:vetProducts[0].unit,qty:1}]}));
  }

  function updateProduct(idx,field,value){
    setForm(f=>{
      const products=[...(f?.products||[])];
      products[idx]={...products[idx],[field]:value};
      if(field==="stockId"){
        const found=items.find(i=>String(i.id)===String(value));
        if(found){products[idx].name=found.name;products[idx].unit=found.unit;}
      }
      return {...f,products};
    });
  }

  function removeProduct(idx){
    setForm(f=>({...f,products:(f.products||[]).filter((_,i)=>i!==idx)}));
  }

  function addAnimal(value){
    const nextValue = String(value||"").trim();
    if(!nextValue) return;
    const foundAnimal = animals.find(animal => String(animal?.id||"") === nextValue || String(animal?.tag||"") === nextValue);
    if(!foundAnimal) return;
    const nextId = String(foundAnimal.id || foundAnimal.tag || nextValue);
    const nextTag = String(foundAnimal.tag || "").trim();
    const nextName = String(foundAnimal.name || foundAnimal.tag || `Animal ${nextId}`).trim();
    setForm(f=>{
      const currentIds = Array.isArray(f?.animalIds) ? f.animalIds.map(v=>String(v)) : [];
      const currentTags = Array.isArray(f?.animalTags) ? f.animalTags.map(v=>String(v||"").trim()).filter(Boolean) : [];
      const currentNames = Array.isArray(f?.animalNames) ? f.animalNames.map(v=>String(v||"").trim()).filter(Boolean) : [];
      if(currentIds.includes(nextId) || (nextTag && currentTags.includes(nextTag))) return f;
      const animalIds = [...currentIds];
      const animalTags = [...currentTags, nextTag].filter(Boolean);
      const animalNames = [...currentNames, nextName].filter(Boolean);
      return {
        ...f,
        animalIds,
        animalTags,
        animalNames,
        animalTag: animalTags.join(", "),
        animalName: animalNames.join(", "),
      };
    });
  }

  function removeAnimal(value){
    const targetValue = String(value||"").trim();
    if(!targetValue) return;
    setForm(f=>{
      const currentIds = Array.isArray(f?.animalIds) ? f.animalIds.map(v=>String(v)) : [];
      const currentTags = Array.isArray(f?.animalTags) ? f.animalTags.map(v=>String(v||"").trim()).filter(Boolean) : [];
      const currentNames = Array.isArray(f?.animalNames) ? f.animalNames.map(v=>String(v||"").trim()).filter(Boolean) : [];
      const nextItems = currentIds.map((id, index)=>({
        id,
        tag: currentTags[index] || "",
        name: currentNames[index] || "",
      })).filter(item => item.id !== targetValue && item.tag !== targetValue);
      const animalIds = nextItems.map(item=>item.id);
      const animalTags = nextItems.map(item=>item.tag).filter(Boolean);
      const animalNames = nextItems.map(item=>item.name).filter(Boolean);
      return {
        ...f,
        animalIds,
        animalTags,
        animalNames,
        animalTag: animalTags.join(", "),
        animalName: animalNames.join(", "),
      };
    });
  }

  function addProcedure(value){
    const nextValue = String(value||"").trim();
    if(!nextValue) return;
    setForm(f=>{
      const current = Array.isArray(f?.procedures) ? f.procedures : [];
      if(current.includes(nextValue)) return f;
      const procedures = [...current, nextValue];
      return { ...f, procedures, type: procedures[0] || nextValue };
    });
  }

  function removeProcedure(value){
    setForm(f=>{
      const procedures = (Array.isArray(f?.procedures) ? f.procedures : []).filter(item=>String(item)!==String(value));
      return { ...f, procedures, type: procedures[0] || vetTypeOptions[0] || VET_TYPES[0] };
    });
  }

  function openNewRecord(prefill={}, mode="individual"){
    setSelectedRecordId("");
    setVetView(mode === "lot" ? "lot" : "individual");
    setForm({...defForm,...prefill,entryMode:mode === "lot" ? "lot" : "individual",_new:true,products:prefill.products||[],photos:Array.isArray(prefill.photos)?prefill.photos:[],procedures:Array.isArray(prefill.procedures)?prefill.procedures:(prefill.type?[prefill.type]:[]),lotIds:Array.isArray(prefill.lotIds)?prefill.lotIds:[],animalIds:Array.isArray(prefill.animalIds)?prefill.animalIds.map(v=>String(v)):[],animalTags:Array.isArray(prefill.animalTags)?prefill.animalTags.map(v=>String(v||"").trim()).filter(Boolean):(prefill.animalTag?[String(prefill.animalTag).trim()].filter(Boolean):[]),animalNames:Array.isArray(prefill.animalNames)?prefill.animalNames.map(v=>String(v||"").trim()).filter(Boolean):(prefill.animalName?[String(prefill.animalName).trim()].filter(Boolean):[]),workflowHistory:Array.isArray(prefill.workflowHistory)?prefill.workflowHistory:[]});
  }

  function normalizeVetFormRecord(record, action="detail"){
    const next={...record,entryMode:record.entryMode||"individual",lotIds:Array.isArray(record.lotIds)?record.lotIds:[],animalIds:Array.isArray(record.animalIds)?record.animalIds.map(v=>String(v)):[],animalTags:Array.isArray(record.animalTags)&&record.animalTags.length?record.animalTags.map(v=>String(v||"").trim()).filter(Boolean):(record.animalTag?[String(record.animalTag).trim()].filter(Boolean):[]),animalNames:Array.isArray(record.animalNames)&&record.animalNames.length?record.animalNames.map(v=>String(v||"").trim()).filter(Boolean):(record.animalName?[String(record.animalName).trim()].filter(Boolean):[]),products:record.products||[],photos:Array.isArray(record.photos)?record.photos:[],procedures:Array.isArray(record.procedures)&&record.procedures.length?record.procedures:(record.type?[record.type]:[]),priority:record.priority||"normal",workflowHistory:Array.isArray(record.workflowHistory)?record.workflowHistory:[],_new:false};
    if(action==="medication"){
      next.type = pickVetType("med", record.type || next.type);
      next.procedures = Array.from(new Set([...(next.procedures||[]), next.type]));
      next.notes = next.notes || "Medicamentos registrados no atendimento.";
    }
    if(action==="schedule"){
      next.nextDate = next.nextDate || today();
      next.notes = next.notes || "Retorno/agendamento planejado.";
    }
    if(action==="curative"){
      next.type = pickVetType("curativo", pickVetType("consulta", record.type || next.type));
      next.notes = next.notes || "Curativos e cuidados pós-atendimento.";
    }
    return next;
  }

  function openEditRecord(record, action="detail"){
    const next = normalizeVetFormRecord(record, action);
    setVetView(next.entryMode === "lot" ? "lot" : "individual");
    setSelectedRecordId(String(record.id||""));
    setForm(next);
  }

  function appendVetWorkflowEvent(record, event){
    const nextEvent = {
      id:createClientId("vetflow"),
      type:event?.type || "update",
      label:event?.label || "Atualização",
      at:new Date().toISOString(),
      by:session?.name || "",
      notes:event?.notes || "",
    };
    return [ ...(Array.isArray(record?.workflowHistory) ? record.workflowHistory : []), nextEvent ];
  }

  function updateVetRecord(recordId, patch, successMessage, workflowEvent=null){
    const actorName = session?.name || "";
    setRecords(prev=>prev.map(record=>{
      if(String(record.id)!==String(recordId)) return record;
      const nextRecord = markOperationalUpdatePending(stampRecordGeneration({
        ...record,
        ...patch,
        workflowHistory: workflowEvent ? appendVetWorkflowEvent(record, workflowEvent) : (Array.isArray(record.workflowHistory) ? record.workflowHistory : []),
         ...makeUpdateMeta(actorName),
      }, currentFarm), { userName: actorName });
      if(String(record?.status || "").trim() !== String(nextRecord?.status || "").trim()){
        const liveDisplay = buildVetLiveActivityDisplay(nextRecord, { status: nextRecord?.status || "" });
        if(isVetLiveActivityEnabled()){
          notifyOperationalActivity({
            module: LIVE_ACTIVITY_MODULES.VETERINARY,
            eventType: LIVE_ACTIVITY_EVENT_TYPES.STATUS_CHANGED,
            record: nextRecord,
            title: liveDisplay.displayTitle || workflowEvent?.label || "Status do atendimento atualizado",
            status: nextRecord?.status || "",
            previousStatus: record?.status || "",
            farmId: nextRecord?.farmId || currentFarmId || "",
            farmName: nextRecord?.farmName || currentFarm?.name || "",
            extraFarmShortCode: currentFarm?.shortCode || currentFarm?.sigla || nextRecord?.farmShortCode || nextRecord?.farmSigla || "",
            updatedAt: nextRecord?.updatedAt || new Date().toISOString(),
            actor: actorName,
            priority: nextRecord?.priority || "normal",
            ...liveDisplay,
            extra: { event: workflowEvent?.type || "vet_status_changed", recordNumber: nextRecord?.recordNumber || "", ...liveDisplay },
          });
        }
      }
      return nextRecord;
    }));
    setSelectedRecordId(String(recordId||""));
    syncOnSave("vet");
    if(successMessage) show(successMessage);
  }

  function startVetAttendance(record){
    updateVetRecord(record.id, {
      status:"Em andamento",
      workflowStartedAt: record?.workflowStartedAt || new Date().toISOString(),
      pausedAt:"",
      pauseReason:"",
      pauseNote:"",
      completedAt:"",
    }, "▶️ Atendimento iniciado.", { type:"started", label:"Atendimento iniciado" });
  }

  function restartVetAttendance(record){
    updateVetRecord(record.id, {
      status:"Em andamento",
      pausedAt:"",
      pauseReason:"",
      pauseNote:"",
      completedAt:"",
    }, "🔄 Atendimento retomado.", { type:"resumed", label:"Atendimento retomado" });
  }

  function completeVetAttendance(record){
    updateVetRecord(record.id, {
      status:"Concluído",
      completedAt: new Date().toISOString(),
      pausedAt:"",
    }, "✅ Atendimento concluído.", { type:"completed", label:"Atendimento concluído" });
  }

  function openVetAgendaShortcut(record, action){
    const actorName = session?.name || "";
    const commitAndOpen = (patch, successMessage, workflowEvent=null, nextAction="detail")=>{
      const nextRecord = markOperationalUpdatePending(stampRecordGeneration({
        ...record,
        ...patch,
        workflowHistory: workflowEvent ? appendVetWorkflowEvent(record, workflowEvent) : (Array.isArray(record.workflowHistory) ? record.workflowHistory : []),
         ...makeUpdateMeta(actorName),
      }, currentFarm), { userName: actorName });
      setRecords(prev=>prev.map(item=>String(item.id)!==String(record.id) ? item : nextRecord));
      setVetView(nextRecord.entryMode === "lot" ? "lot" : "individual");
      setSelectedRecordId(String(nextRecord.id||""));
      setForm(normalizeVetFormRecord(nextRecord, nextAction));
      setVetPauseForm(null);
      setVetMaterialForm(null);
      setVetPhotoForm(null);
      syncOnSave("vet");
      if(String(record?.status || "").trim() !== String(nextRecord?.status || "").trim()){
        const liveDisplay = buildVetLiveActivityDisplay(nextRecord, { status: nextRecord?.status || "" });
        if(isVetLiveActivityEnabled()){
          notifyOperationalActivity({
            module: LIVE_ACTIVITY_MODULES.VETERINARY,
            eventType: LIVE_ACTIVITY_EVENT_TYPES.STATUS_CHANGED,
            record: nextRecord,
            title: liveDisplay.displayTitle || workflowEvent?.label || "Status do atendimento atualizado",
            status: nextRecord?.status || "",
            previousStatus: record?.status || "",
            farmId: nextRecord?.farmId || currentFarmId || "",
            farmName: nextRecord?.farmName || currentFarm?.name || "",
            extraFarmShortCode: currentFarm?.shortCode || currentFarm?.sigla || nextRecord?.farmShortCode || nextRecord?.farmSigla || "",
            updatedAt: nextRecord?.updatedAt || new Date().toISOString(),
            actor: actorName,
            priority: nextRecord?.priority || "normal",
            ...liveDisplay,
            extra: { event: workflowEvent?.type || "vet_status_changed", recordNumber: nextRecord?.recordNumber || "", ...liveDisplay },
          });
        }
      }
      if(successMessage) show(successMessage);
      return nextRecord;
    };

    if(action==="start"){
      return commitAndOpen({
        status:"Em andamento",
        workflowStartedAt: record?.workflowStartedAt || new Date().toISOString(),
        pausedAt:"",
        pauseReason:"",
        pauseNote:"",
        completedAt:"",
      }, "▶️ Atendimento iniciado.", { type:"started", label:"Atendimento iniciado" });
    }

    if(action==="restart"){
      return commitAndOpen({
        status:"Em andamento",
        pausedAt:"",
        pauseReason:"",
        pauseNote:"",
        completedAt:"",
      }, "🔄 Atendimento retomado.", { type:"resumed", label:"Atendimento retomado" });
    }

    if(action==="complete"){
      return commitAndOpen({
        status:"Concluído",
        completedAt: new Date().toISOString(),
        pausedAt:"",
      }, "✅ Atendimento concluído.", { type:"completed", label:"Atendimento concluído" });
    }

    openEditRecord(record);
    if(action==="pause"){
      setVetPauseForm({id:record.id,reason:(vetPauseReasonOptions[0]||""),obs:""});
      setVetMaterialForm(null);
      setVetPhotoForm(null);
    }
    return record;
  }

// eslint-disable-next-line no-unused-vars
  function addVetPhotos(recordId, files){
    const queue = Array.from(files||[]);
    if(!queue.length) return;
    Promise.all(queue.map(async file=>({
      id:createClientId("vetimg"),
      title:"Foto do Atendimento",
      photo:await compressImage(file, 1400, 0.78),
      thumb:await compressImage(file, 320, 0.64),
      by:session?.name||"",
      ts:new Date().toISOString(),
      name:file.name||""
    }))).then(newPhotos=>{
      updateVetRecord(recordId, {
        photos:[...(normalizedRecords.find(r=>String(r.id)===String(recordId))?.photos||[]), ...newPhotos],
      }, "📷 Fotos adicionadas ao atendimento.", { type:"photos", label:"Fotos adicionadas", notes:`${newPhotos.length} arquivo(s)` });
      setVetPhotoForm(null);
    });
  }

  function save(){
    if(!canEdit){show("Sem permissão para alterar registros veterinários.","error");return;}
    const entryMode = String(form?.entryMode || "individual");
    if(entryMode === "individual" && !(Array.isArray(form?.animalIds) && form.animalIds.length) && !String(form?.animalTag||"").trim()){show("Selecione ao menos um animal.","error");return;}
    if(entryMode === "lot" && !(Array.isArray(form?.lotIds) && form.lotIds.length)){show("Selecione ao menos um lote.","error");return;}
    const prods=form.products||[];
    for(const prod of prods){
      const stock=items.find(i=>String(i.id)===String(prod.stockId));
      if(!stock){show("Produto não encontrado no estoque.","error");return;}
      if(Number(prod.qty||0)<=0){show(`Quantidade inválida para ${prod.name || 'produto'}.`,"error");return;}
    }
    if(prods.length>0){
      let updatedStock=[...items];
      prods.forEach(prod=>{
        updatedStock=updatedStock.map(i=>String(i.id)===String(prod.stockId)?{...i,updatedAt:new Date().toISOString()}:i);
      });
      setItems(updatedStock);
      syncOnSave("stock");
    }
    const {_new,...d}=form;
    const actorName = session?.name || "";
    const selectedLots = (d.lotIds||[]).map(lotId=>lotOptions.find(l=>String(l.id)===String(lotId))).filter(Boolean);
    const lotSummary = selectedLots.map(formatLotLabel).join(" • ");
    const normalizedProcedures = Array.isArray(d?.procedures) && d.procedures.length ? d.procedures : (d?.type ? [d.type] : []);
    const normalizedAnimalIds = Array.isArray(d?.animalIds) ? d.animalIds.map(v=>String(v)) : [];
    const normalizedAnimalTags = Array.isArray(d?.animalTags) && d.animalTags.length ? d.animalTags.map(v=>String(v||"").trim()).filter(Boolean) : (d?.animalTag ? [String(d.animalTag).trim()].filter(Boolean) : []);
    const normalizedAnimalNames = Array.isArray(d?.animalNames) && d.animalNames.length ? d.animalNames.map(v=>String(v||"").trim()).filter(Boolean) : (d?.animalName ? [String(d.animalName).trim()].filter(Boolean) : []);
    const basePayload = {
      ...d,
      entryMode,
      type: normalizedProcedures[0] || d.type,
      procedures: normalizedProcedures,
      photos: Array.isArray(d?.photos) ? d.photos : [],
      status: d?.status || (d?.nextDate ? "Agendado" : "Concluído"),
      priority: d?.priority || "normal",
      workflowStartedAt: d?.workflowStartedAt || "",
      pausedAt: d?.pausedAt || "",
      pauseReason: d?.pauseReason || "",
      pauseNote: d?.pauseNote || "",
      completedAt: d?.completedAt || "",
      workflowHistory: Array.isArray(d?.workflowHistory) ? d.workflowHistory : [],
      animalIds: entryMode === "lot" ? [] : normalizedAnimalIds,
      animalTags: entryMode === "lot" ? [] : normalizedAnimalTags,
      animalNames: entryMode === "lot" ? [] : normalizedAnimalNames,
      animalTag: entryMode === "lot" ? "" : normalizedAnimalTags.join(", "),
      animalName: entryMode === "lot" ? (d.animalName || selectedLots.map(l=>l.name||l.code||"Lote").join(", ")) : normalizedAnimalNames.join(", "),
      lotIds: entryMode === "lot" ? (d.lotIds||[]).map(v=>String(v)) : [],
      lotSummary: entryMode === "lot" ? lotSummary : "",
    };
    if(_new){
      const recordId = String(basePayload.id || createClientId("vet"));
      const clientId = String(basePayload.clientId || recordId);
      const createdRecord = markOperationalCreatePending(stampRecordGeneration({ ...basePayload, ...makeProvisionalVetRecordNumber(basePayload), id:recordId, clientId, farmId:currentFarmId||basePayload.farmId||"", workflowHistory: appendVetWorkflowEvent(basePayload, { type:"created", label:"Atendimento cadastrado", notes: basePayload.status || "Agendado" }),  ...makeCreateMeta(actorName) }, currentFarm), { userName: actorName });
      setRecords(p=>[...p, createdRecord]);
      const entryLabel = createdRecord.entryMode === "lot" ? "Atendimento em lote realizado" : "Atendimento individual realizado";
      appendVetNotification(createdRecord, { title: entryLabel, eventLabel: entryLabel, summary:`${session?.name || "Sistema"} registrou o atendimento ${createdRecord.recordNumber || createdRecord.id}${createdRecord.entryMode === "lot" ? " em lote" : ""}.`, status: createdRecord.status || "Concluído", eventAt: createdRecord.updatedAt || createdRecord.createdAt || new Date().toISOString() });
      setSelectedRecordId(recordId);
    }else{
      setRecords(p=>p.map(r=>String(r.id)===String(basePayload.id)?markOperationalUpdatePending({...r,...basePayload,clientId:String(r.clientId||basePayload.clientId||r.id||basePayload.id||createClientId("vet")),farmId:r.farmId||basePayload.farmId||currentFarmId||"",_deleted:false,deletedAt:"",deleted_at:"",deletedBy:"",...makeUpdateMeta(actorName,{ deletedAt:"", deleted_at:"", deletedBy:"" })}, { userName: actorName }):r));
      setSelectedRecordId(String(basePayload.id||""));
    }
    show(entryMode === "lot" ? "✅ Atendimento em lote salvo." : "✅ Atendimento salvo.");
    setForm(null);
    syncOnSave("vet");
  }

  function deleteRecord(id){
    if(!canDelete){show("Sem permissão para excluir registros veterinários.","error");return;}
    const rec=normalizedRecords.find(r=>String(r.id)===String(id));
    if(rec?.products?.length){
      setItems(p=>p.map(i=>{
        const used=rec.products.find(pp=>String(pp.stockId)===String(i.id));
        return used?{...i,quantity:i.quantity+used.qty}:i;
      }));
      syncOnSave("stock");
    }
    setRecords(p=>p.map(r=>String(r.id)!==String(id) ? r : markOperationalUpdatePending({...r,_deleted:true,deletedAt:new Date().toISOString(),deleted_at:new Date().toISOString(),deletedBy:session?.name||"",updatedAt:new Date().toISOString(),updatedBy:session?.name||""}, { userName: session?.name || "", operationType:"delete" })));
    if(String(selectedRecordId)===String(id)) setSelectedRecordId("");
    setForm(null);
    syncOnSave("vet");
    show("🗑️ Atendimento excluído.");
  }

  const renderVetInlineEditor = (anchorStyle={}) => form ? <div style={{...panelStyle, ...anchorStyle}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:isCompactViewport()?"flex-start":"center",gap:12,flexWrap:"wrap",marginBottom:14}}>
        <div>
          <div style={{fontSize:11,fontWeight:800,color:T.accent,textTransform:"uppercase",letterSpacing:.8}}>Atendimentos</div>
          <div style={{fontSize:isCompactViewport()?18:20,fontWeight:800,color:T.text,marginTop:4}}>{form._new ? (form.entryMode === "lot" ? "Novo atendimento em lote" : "Novo atendimento individual") : (form.entryMode === "lot" ? "Editar atendimento em lote" : "Editar atendimento individual")}</div>
          <div style={{fontSize:12,color:T.muted,marginTop:4}}>Abra o fluxo exatamente abaixo do registro e finalize tudo sem sair do contexto.</div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {!form._new && canDelete && <Btn variant="danger" onClick={()=>deleteRecord(form.id)}>Excluir</Btn>}
          <Btn variant="ghost" onClick={()=>{ setForm(null); setSelectedRecordId(""); setVetPauseForm(null); setVetMaterialForm(null); setVetPhotoForm(null); }}>← Fechar</Btn>
          {((form && form._new && canCreate) || (form && !form._new && canEdit)) && <Btn onClick={save}>{form.entryMode === "lot" ? "Salvar lote" : "Salvar atendimento"}</Btn>}
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
        {form.entryMode === "individual" ? <Field label="Animais *" col="1/-1">
          <Select value="" onChange={e=>{ addAnimal(e.target.value); e.target.value=""; }}>
            <option value="">Selecione um animal</option>
            {animals.map(animal=><option key={animal.id || animal.tag} value={animal.id || animal.tag}>{`${animal.tag || "Sem brinco"} · ${animal.name || "Animal sem nome"}`}</option>)}
          </Select>
          <div style={{fontSize:12,color:T.muted,marginTop:6}}>Você pode selecionar mais de um animal para o mesmo atendimento individual.</div>
          {!!(form.animalIds||[]).length && <div style={{display:"grid",gap:10,marginTop:10}}>{(form.animalIds||[]).map((animalId, index)=>{
            const animal = animals.find(item=>String(item.id||"")===String(animalId)) || animals.find(item=>String(item.tag||"")===String((form.animalTags||[])[index]||""));
            const tag = animal?.tag || (form.animalTags||[])[index] || "Sem brinco";
            const name = animal?.name || (form.animalNames||[])[index] || "Animal sem nome";
            const category = animal?.category || animal?.species || animal?.type || animal?.animalType || "Não informada";
            const lotLabel = animal ? ((Array.isArray(lots)?lots:[]).find(lot=>String(lot?.id||"")===String(animal?.lotId||""))?.name || (Array.isArray(lots)?lots:[]).find(lot=>String(lot?.id||"")===String(animal?.lotId||""))?.code || "Sem lote") : "Sem lote";
            return <div key={animalId} style={{position:"relative",background:T.card2,border:`1px solid ${T.border}`,borderRadius:14,padding:"12px 14px"}}>
              <button type="button" onClick={()=>removeAnimal(animalId)} style={{position:"absolute",top:8,right:8,width:24,height:24,borderRadius:999,border:`1px solid ${T.border}`,background:T.card,color:T.muted,cursor:"pointer",fontSize:14,lineHeight:"22px",padding:0}}>×</button>
              <div style={{fontSize:14,fontWeight:800,color:T.text,marginBottom:8,paddingRight:24}}>{name}</div>
              <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(2,minmax(0,1fr))",gap:8}}>
                <div style={{fontSize:12,color:T.muted}}>Brinco<div style={{fontSize:13,color:T.text,fontWeight:700,marginTop:2}}>{tag}</div></div>
                <div style={{fontSize:12,color:T.muted}}>Categoria<div style={{fontSize:13,color:T.text,fontWeight:700,marginTop:2}}>{category}</div></div>
                <div style={{fontSize:12,color:T.muted}}>Lote<div style={{fontSize:13,color:T.text,fontWeight:700,marginTop:2}}>{lotLabel}</div></div>
              </div>
            </div>;
          })}</div>}
        </Field> : <Field label="Lotes *" col="1/-1">
          <Select multiple value={(form.lotIds||[]).map(String)} onChange={e=>setForm(f=>({...f,lotIds:[...e.target.selectedOptions].map(opt=>String(opt.value))}))} style={{minHeight:140}}>
            {lotOptions.map(lot=><option key={lot.id} value={lot.id}>{formatLotLabel(lot)}</option>)}
          </Select>
          <div style={{fontSize:12,color:T.muted,marginTop:6}}>Você pode selecionar mais de um lote ao mesmo tempo.</div>
          {!!(form.lotIds||[]).length && <div style={{display:"grid",gap:10,marginTop:10}}>{(form.lotIds||[]).map(lotId=>{
            const lot = lotOptions.find(item=>String(item.id)===String(lotId));
            const qty = lot ? lotCurrentQty(lot) : "—";
            const location = lot ? (pastureMap[String(lot?.pastureId||"")] || lot?.pastureName || lot?.location || lot?.where || "Sem localização") : "Sem localização";
            const age = lot ? (lot?.ageLabel || ageRangeMap[String(lot?.ageRange || lot?.currentAgeRange || lot?.ageRangeId || "")] || lot?.birdStageLabel || lot?.birdStage || lot?.era || "Faixa não informada") : "Faixa não informada";
            const species = lot ? (lot?.species || lot?.animalType || lot?.category || lot?.type || "Não informada") : "Não informada";
            return <div key={lotId} style={{position:"relative",background:T.card2,border:`1px solid ${T.border}`,borderRadius:14,padding:"12px 14px"}}>
              <button type="button" onClick={()=>setForm(f=>({...f,lotIds:(f.lotIds||[]).filter(id=>String(id)!==String(lotId))}))} style={{position:"absolute",top:8,right:8,width:24,height:24,borderRadius:999,border:`1px solid ${T.border}`,background:T.card,color:T.muted,cursor:"pointer",fontSize:14,lineHeight:"22px",padding:0}}>×</button>
              <div style={{fontSize:14,fontWeight:800,color:T.text,marginBottom:8,paddingRight:24}}>{lot?.name || lot?.code || `Lote ${lotId}`}</div>
              <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(2,minmax(0,1fr))",gap:8}}>
                <div style={{fontSize:12,color:T.muted}}>Espécie<div style={{fontSize:13,color:T.text,fontWeight:700,marginTop:2}}>{species}</div></div>
                <div style={{fontSize:12,color:T.muted}}>Faixa de idade<div style={{fontSize:13,color:T.text,fontWeight:700,marginTop:2}}>{age}</div></div>
                <div style={{fontSize:12,color:T.muted}}>Localização<div style={{fontSize:13,color:T.text,fontWeight:700,marginTop:2}}>{location}</div></div>
                <div style={{fontSize:12,color:T.muted}}>Quantidade<div style={{fontSize:13,color:T.text,fontWeight:700,marginTop:2}}>{qty} animal(is)</div></div>
              </div>
            </div>;
          })}</div>}
        </Field>}
        <Field label="Procedimentos" col="1/-1">
          <Select value="" onChange={e=>{ addProcedure(e.target.value); e.target.value=""; }}>
            <option value="">Selecione um procedimento</option>
            {vetTypeOptions.map(t=><option key={t} value={t}>{t}</option>)}
          </Select>
          {!!(form.procedures||[]).length && <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:10}}>{(form.procedures||[]).map(proc=><div key={proc} style={{position:"relative",padding:"10px 28px 10px 12px",borderRadius:999,border:`1px solid ${T.border}`,background:T.card2,fontSize:12,fontWeight:700,color:T.text}}>{proc}<button type="button" onClick={()=>removeProcedure(proc)} style={{position:"absolute",top:-4,right:-4,width:20,height:20,borderRadius:999,border:`1px solid ${T.border}`,background:T.card,color:T.muted,cursor:"pointer",fontSize:12,lineHeight:"18px",padding:0}}>×</button></div>)}</div>}
        </Field>
        <Field label="Data"><Input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))}/></Field>
        <Field label="Responsável"><Input value={form.vet} onChange={e=>setForm(f=>({...f,vet:e.target.value}))} placeholder="Nome do responsável"/></Field>
        <Field label="Prioridade">
          <Select value={form.priority || "normal"} onChange={e=>setForm(f=>({...f,priority:e.target.value}))}>
            {VET_PRIORITIES.map(p=><option key={p.id} value={p.id}>{p.label}</option>)}
          </Select>
        </Field>
        <Field label="Data de retorno"><Input type="date" value={form.nextDate} onChange={e=>setForm(f=>({...f,nextDate:e.target.value}))}/></Field>
        <Field label="Observações" col="1/-1"><Textarea rows={3} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Sintomas, conduta, resposta ao tratamento, observações de campo..."/></Field>
        <Field label="💊 Produtos utilizados" col="1/-1">
          <div style={{marginTop:6}}>
            {vetProducts.length===0&&<div style={{color:T.muted,fontSize:12,background:T.card2,borderRadius:8,padding:"10px 14px"}}>Nenhum produto marcado como uso veterinário no estoque. Acesse o módulo Estoque e marque os produtos desejados.</div>}
            {(form.products||[]).map((prod,idx)=>{
              const stock=items.find(i=>String(i.id)===String(prod.stockId));
              const stockQty=stock?.quantity??0;
              const willBeNeg=stockQty-Number(prod.qty||0)<0;
              return(
                <div key={idx} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr auto auto auto",gap:8,alignItems:"center",marginBottom:8,background:T.card2,borderRadius:10,padding:"10px 12px"}}>
                  <Select value={prod.stockId} onChange={e=>updateProduct(idx,"stockId",Number(e.target.value))}>
                    {vetProducts.map(vp=><option key={vp.id} value={vp.id}>{vp.name} — {vp.quantity} {vp.unit} disponível</option>)}
                  </Select>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <Input type="number" min="0.01" step="0.01" value={prod.qty} onChange={e=>updateProduct(idx,"qty",Number(e.target.value))} style={{width:90}}/>
                    <span style={{fontSize:12,color:T.muted,whiteSpace:"nowrap"}}>{prod.unit}</span>
                  </div>
                  {willBeNeg?<span style={{fontSize:11,color:T.red,whiteSpace:"nowrap"}}>⚠️ Ficará negativo</span>:<span style={{fontSize:11,color:T.green,whiteSpace:"nowrap"}}>Restará: {(stockQty-Number(prod.qty||0)).toFixed(2)}</span>}
                  <button onClick={()=>removeProduct(idx)} style={{background:"none",border:"none",cursor:"pointer",color:T.red,fontSize:16,padding:"0 4px"}}>✕</button>
                </div>
              );
            })}
            {vetProducts.length>0&&<Btn variant="ghost" size="sm" onClick={addProduct}>+ Adicionar Produto</Btn>}
          </div>
        </Field>
      </div>
    </div> : null;

  const todayDateKey = today();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  const vetAgendaEntries = React.useMemo(()=>{
    const entries = [];
    normalizedRecords.forEach(record=>{
      const performedDate = toDateInputValue(record?.completedAt || record?.date);
      const nextDate = toDateInputValue(record?.nextDate);
      const statusText = normalizeText(record?.status || "");
      if(statusText.includes("andamento")){
        entries.push({ key:`${record.id}:progress:${nextDate || performedDate || todayDateKey}`, dateKey:nextDate || performedDate || todayDateKey, status:"andamento", color:T.orange||T.yellow, label:"Em andamento", record });
        return;
      }
      if(statusText.includes("paus")){
        entries.push({ key:`${record.id}:paused:${nextDate || performedDate || todayDateKey}`, dateKey:nextDate || performedDate || todayDateKey, status:"andamento", color:T.orange||T.yellow, label:"Pausado", record });
        return;
      }
      if(statusText.includes("agend")){
        const targetDate = nextDate || performedDate;
        if(targetDate){
          if(targetDate < todayDateKey){
            entries.push({ key:`${record.id}:missed:${targetDate}`, dateKey:targetDate, status:"nao_realizado", color:T.red, label:"Não realizado", record });
          }else{
            entries.push({ key:`${record.id}:scheduled:${targetDate}`, dateKey:targetDate, status:"agendado", color:T.blue||T.accent, label:"Agendado", record });
          }
        }
        return;
      }
      if(performedDate){
        entries.push({ key:`${record.id}:done:${performedDate}`, dateKey:performedDate, status:"realizado", color:T.green, label:"Concluído", record });
      }
    });
    return entries;
  }, [normalizedRecords, normalizeText, todayDateKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const vetAgendaStats = React.useMemo(()=>({
    vet_agendados: vetAgendaEntries.filter(item=>item.status==="agendado").length,
    vet_realizados: vetAgendaEntries.filter(item=>item.status==="realizado").length,
    vet_nao_realizados: vetAgendaEntries.filter(item=>item.status==="nao_realizado").length,
    vet_em_andamento: vetAgendaEntries.filter(item=>item.status==="andamento").length,
  }), [vetAgendaEntries]);
  const vetAgendaMarkerMap = React.useMemo(()=> vetAgendaEntries.reduce((acc, item)=>{ (acc[item.dateKey] ||= []).push(item); return acc; }, {}), [vetAgendaEntries]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedAgendaDate = React.useMemo(()=>{ const value = toDateInputValue(vetAgendaSelectedDate) || todayDateKey; return value; }, [vetAgendaSelectedDate, todayDateKey]);
// eslint-disable-next-line no-unused-vars
  const _selectedAgendaDateObj = React.useMemo(()=> new Date(`${selectedAgendaDate}T12:00:00`), [selectedAgendaDate]);
  const vetAgendaSelectedItems = React.useMemo(()=> vetAgendaMarkerMap[selectedAgendaDate] || [], [vetAgendaMarkerMap, selectedAgendaDate]);
  const getVetStatusMeta = React.useCallback((statusRaw)=>{
    const value = normalizeText(statusRaw || "");
    if(value.includes("andamento")) return { label:"Em andamento", color:T.orange||T.yellow, shortLabel:"Em andamento" };
    if(value.includes("paus")) return { label:"Pausado", color:T.orange||T.yellow, shortLabel:"Pausado" };
    if(value.includes("agend")) return { label:"Agendado", color:T.blue||T.accent, shortLabel:"Agendado" };
    return { label:"Concluído", color:T.green, shortLabel:"Concluído" };
  }, [normalizeText]);

  const canStartVetRecord = React.useCallback((record)=> normalizeText(record?.status || "").includes("agend"), [normalizeText]);
  const canPauseVetRecord = React.useCallback((record)=> normalizeText(record?.status || "").includes("andamento"), [normalizeText]);
  const canRestartVetRecord = React.useCallback((record)=> normalizeText(record?.status || "").includes("paus"), [normalizeText]);
  const canCompleteVetRecord = React.useCallback((record)=> !normalizeText(record?.status || "").includes("conclu") && !normalizeText(record?.status || "").includes("realizado"), [normalizeText]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  const getVetPrimaryAction = React.useCallback((record)=>{
    if(canStartVetRecord(record)) return { id:"start", label:"Iniciar", background:`${T.green}22`, color:T.green, border:`1px solid ${T.green}55`, onClick:()=>startVetAttendance(record) };
    if(canPauseVetRecord(record)) return { id:"pause", label:"Pausar", background:`${T.orange||T.yellow}20`, color:T.orange||T.yellow, border:`1px solid ${(T.orange||T.yellow)}55`, onClick:()=>setVetPauseForm({id:record.id,reason:(vetPauseReasonOptions[0]||""),obs:""}) };
    if(canRestartVetRecord(record)) return { id:"restart", label:"Recomeçar", background:`${T.green}18`, color:T.green, border:`1px solid ${T.green}55`, onClick:()=>restartVetAttendance(record) };
    return null;
  }, [T.green, T.orange, T.yellow, canPauseVetRecord, canRestartVetRecord, canStartVetRecord, startVetAttendance, vetPauseReasonOptions, restartVetAttendance]); // eslint-disable-line react-hooks/exhaustive-deps

  const focusedVetRecordId = String(selectedRecordId || (form && !form._new ? form.id : "") || vetPauseForm?.id || vetMaterialForm || vetPhotoForm || "");
  const isVetInlineFocusMode = !!(form || selectedRecordId || vetPauseForm || vetMaterialForm || vetPhotoForm);

  const vetAgendaCards = [
    { id:"vet_agendados", icon:"🔵", label:"Agendados", value:vetAgendaStats.vet_agendados, color:T.blue||T.accent },
    { id:"vet_realizados", icon:"🟢", label:"Realizados", value:vetAgendaStats.vet_realizados, color:T.green },
    { id:"vet_nao_realizados", icon:"🔴", label:"Não realizados", value:vetAgendaStats.vet_nao_realizados, color:T.red },
    { id:"vet_em_andamento", icon:"🟡", label:"Em andamento", value:vetAgendaStats.vet_em_andamento, color:T.orange||T.yellow },
  ].filter(card=>allowedVetAgendaCards.includes(card.id));
  const renderVetAgendaItems = (items, title, emptyText)=>{ const visibleItems = isVetInlineFocusMode ? (focusedVetRecordId ? items.filter(item=>String(item?.record?.id||"")===focusedVetRecordId) : []) : items; return <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:20,padding:isCompactViewport()?14:18}}><div style={{fontSize:16,fontWeight:800,color:T.text,marginBottom:12}}>{title}</div>{visibleItems.length ? <div style={{display:"grid",gap:10}}>{visibleItems.map(item=>{ const record=item.record; const isLotRecord = record.entryMode === "lot"; const animalCount = (record.animalIds||[]).length || (record.animalTags||[]).length || (record.animalTag ? 1 : 0); return <div key={item.key} style={{background:T.card2,border:`1px solid ${T.border}`,borderLeft:`4px solid ${item.color}`,borderRadius:14,padding:"12px 14px"}}><div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}><div style={{minWidth:0,flex:1}}><div style={{display:"flex",flexDirection:"column",gap:4,minWidth:0,flex:1}}><div style={{fontSize:10,fontWeight:800,color:T.accent,letterSpacing:.6,marginBottom:4}}>{displayVetRecordNumber(record)}</div><div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}><div style={{fontWeight:800,color:T.text}}>{isLotRecord ? (record.animalName || "Atendimento em lote") : (record.animalName || record.animalTag || "Atendimento veterinário")}</div><Badge color={item.color}>{item.label}</Badge>{(record.procedures||[]).length ? (record.procedures||[]).slice(0,3).map(proc=><Badge key={proc} color={T.accent}>{proc}</Badge>) : <Badge color={T.accent}>{record.type || "Atendimento"}</Badge>}</div></div><div style={{fontSize:12,color:T.muted,marginTop:6}}>{fmtDate(item.dateKey)} · {record.vet || "Sem responsável"} · {isLotRecord ? `${(record.lotIds||[]).length} lote(s)` : (animalCount > 1 ? `${animalCount} animal(is)` : `Brinco ${record.animalTag || "—"}`)}</div>{record.notes ? <div style={{fontSize:12,color:T.muted,marginTop:6}}>{record.notes}</div> : null}</div><div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>{canEdit && canStartVetRecord(record) && <Btn size="sm" onClick={()=>openVetAgendaShortcut(record, "start")} style={{background:`${T.green}22`,color:T.green,border:`1px solid ${T.green}55`}}>Iniciar</Btn>}{canEdit && canPauseVetRecord(record) && <Btn size="sm" variant="ghost" onClick={()=>openVetAgendaShortcut(record, "pause")} style={{background:`${T.orange||T.yellow}18`,color:T.orange||T.yellow,border:`1px solid ${(T.orange||T.yellow)}55`}}>Pausar</Btn>}{canEdit && canRestartVetRecord(record) && <Btn size="sm" variant="ghost" onClick={()=>openVetAgendaShortcut(record, "restart")} style={{background:`${T.green}18`,color:T.green,border:`1px solid ${T.green}55`}}>Recomeçar</Btn>}{canEdit && canCompleteVetRecord(record) && <Btn size="sm" variant="ghost" onClick={()=>openVetAgendaShortcut(record, "complete")} style={{background:`${T.green}18`,color:T.green,border:`1px solid ${T.green}55`}}>Concluir</Btn>}{canEdit && <Btn size="sm" onClick={()=>{ setVetView(record.entryMode === "lot" ? "lot" : "individual"); openEditRecord(record); }}>Editar</Btn>}</div></div></div>; })}</div> : <div style={{color:T.muted,textAlign:"center",padding:24}}>{emptyText}</div>}</div>; };

  const vetSummaryCards = [
    { id:"agenda", icon:"📅", label:"Agenda", value:normalizedRecords.filter(r=>r.nextDate).length, color:T.green },
    { id:"medicamentos", icon:"💊", label:"Medicamentos", value:vetProducts.length, color:T.purple },
    { id:"procedimentos", icon:"🏥", label:"Atendimentos", value:normalizedRecords.length, color:T.accent },
    { id:"historico", icon:"🕘", label:"Histórico", value:normalizedRecords.length, color:T.yellow },
  ].filter(card=>allowedVetCards.includes(card.id));

  const renderRecordCard = (record) => {
    const isSelected = String(selectedRecordId)===String(record.id);
    const isEditingThisRecord = !!form && !form._new && String(form.id||"")===String(record.id);
    const isActiveRecord = isEditingThisRecord || isSelected;
    const meds = (record.products||[]).length;
    const isLotRecord = record.entryMode === "lot";
    const relatedLots = (record.lotIds||[]).map(id=>lotOptions.find(l=>String(l.id)===String(id))).filter(Boolean);
    const relatedAnimals = (record.animalIds||[]).map((id, index)=> animals.find(animal=>String(animal?.id||"")===String(id)) || animals.find(animal=>String(animal?.tag||"")===String((record.animalTags||[])[index]||"")) || ({ id, tag:(record.animalTags||[])[index] || record.animalTag || "", name:(record.animalNames||[])[index] || record.animalName || "Animal" })).filter(Boolean);
    return <React.Fragment key={record.id}><div ref={el=>{ if(el) vetInlineRefs.current.set(String(record.id), el); else vetInlineRefs.current.delete(String(record.id)); }} style={{background:T.card,border:`1px solid ${isActiveRecord?T.accent:T.border}`,borderRadius:16,padding:16,boxShadow:`0 8px 20px ${T.shadow || "rgba(0,0,0,.06)"}`}}>
      <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start"}}>
        <div style={{minWidth:0}}>
          <div style={{fontSize:10,fontWeight:800,color:T.accent,letterSpacing:.6,marginBottom:5}}>{displayVetRecordNumber(record)}</div>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <div style={{fontWeight:800,color:T.text,fontSize:16}}>{isLotRecord ? (record.animalName || "Atendimento em lote") : (record.animalName || 'Animal sem nome')}</div>
            <Badge color={T.accent}>{record.type || 'Atendimento'}</Badge>
            {isLotRecord && <Badge color={T.purple}>Lote</Badge>}
          </div>
          <div style={{fontSize:12,color:T.muted,marginTop:6}}>{isLotRecord ? `${(record.lotIds||[]).length} lote(s)` : ((relatedAnimals.length > 1) ? `${relatedAnimals.length} animal(is)` : <>Brinco <span style={{fontFamily:"monospace",color:T.accent}}>{record.animalTag || '—'}</span></>)} · {fmtDate(record.date)}</div>
        </div>
        {(()=>{ const statusMeta = getVetStatusMeta(record.status); const priorityMeta = getVetPriorityConfig(record.priority || "normal"); return <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>{record.nextDate ? <Badge color={T.yellow}>Agenda {fmtDate(record.nextDate)}</Badge> : null}<Badge color={priorityMeta.color}>{priorityMeta.label}</Badge><Badge color={statusMeta.color}>{statusMeta.label}</Badge></div>; })()}
      </div>
      {(()=>{ const primaryAction = getVetPrimaryAction(record); const canConclude = canCompleteVetRecord(record); return <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)",gap:8,marginTop:12,alignItems:"stretch"}}>
        {canEdit ? <div style={{display:"grid",gap:8}}>
          <Btn size="sm" variant="ghost" disabled={!primaryAction} onClick={()=>primaryAction?.onClick?.()} style={{minHeight:46,justifyContent:"center",fontWeight:800,background:primaryAction?.background || T.card2,color:primaryAction?.color || T.muted,border:primaryAction?.border || `1px solid ${T.border}`,width:"100%"}}>{primaryAction?.label || 'Aguardando'}</Btn>
          <Btn size="sm" variant="ghost" disabled={!canConclude} onClick={()=>canConclude && completeVetAttendance(record)} style={{minHeight:46,justifyContent:"center",fontWeight:800,background:canConclude ? (T.blue||T.accent) : T.card2,color:canConclude ? '#fff' : T.muted,border:`1px solid ${canConclude ? (T.blue||T.accent) : T.border}`,width:"100%"}}>Concluir</Btn>
        </div> : <div style={{display:"grid",gap:8}}>
          <div style={{minHeight:46,background:T.card2,border:`1px solid ${T.border}`,borderRadius:12}} />
          <div style={{minHeight:46,background:T.card2,border:`1px solid ${T.border}`,borderRadius:12}} />
        </div>}
        <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:"10px 12px",minHeight:100,display:"flex",flexDirection:"column",justifyContent:"center"}}>
          <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.6}}>{isLotRecord ? 'Lotes vinculados' : 'Animais'}</div>
          <div style={{fontSize:13,color:T.text,marginTop:6,fontWeight:700}}>{isLotRecord ? `${relatedLots.length} selecionado(s)` : `${relatedAnimals.length || (record.animalTag ? 1 : 0)} selecionado(s)`}</div>
        </div>
      </div>; })()}
      {isLotRecord && <div style={{display:"grid",gap:6,marginTop:12}}>{relatedLots.map(lot=><div key={lot.id} style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:10,padding:"8px 10px",fontSize:12,color:T.text}}>{formatLotLabel(lot)}</div>)}</div>}
      {!isLotRecord && !!relatedAnimals.length && <div style={{display:"grid",gap:6,marginTop:12}}>{relatedAnimals.map((animal, index)=><div key={`${animal.id || animal.tag || index}`} style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:10,padding:"8px 10px",fontSize:12,color:T.text}}>{`${animal.name || 'Animal'}${animal.tag ? ` · ${animal.tag}` : ''}`}</div>)}</div>}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:12}}>
        <Badge color={T.purple}>💊 {meds} produto(s)</Badge>
        {record.notes ? <Badge color={T.accent}>📝 Com observações</Badge> : <Badge color={T.muted}>📝 Sem observações</Badge>}<Badge color={T.blue||T.accent}>📷 {(record.photos||[]).length} foto(s)</Badge>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:14}}>
        {record.vet ? <Badge color={T.muted}>👤 {record.vet}</Badge> : null}
        {canEdit && !isLotRecord && <Btn size="sm" variant="ghost" onClick={()=>openEditRecord(record, "medication")}>Medicamentos</Btn>}
        {canEdit && <Btn size="sm" variant={vetMaterialForm===String(record.id)?"primary":"ghost"} onClick={()=>{ setSelectedRecordId(String(record.id)); setVetMaterialForm(vetMaterialForm===String(record.id)?null:String(record.id)); setVetPhotoForm(null); }}>Material</Btn>}
        {canEdit && <Btn size="sm" variant={vetPhotoForm===String(record.id)?"primary":"ghost"} onClick={()=>{ setSelectedRecordId(String(record.id)); setVetPhotoForm(vetPhotoForm===String(record.id)?null:String(record.id)); setVetMaterialForm(null); }}>Fotos</Btn>}
        {canEdit && <Btn size="sm" variant="ghost" onClick={()=>openEditRecord(record, "schedule")}>Agendamento</Btn>}
        {canEdit && !isLotRecord && <Btn size="sm" variant="ghost" onClick={()=>openEditRecord(record, "curative")}>Curativos</Btn>}
        {canEdit && <Btn size="sm" onClick={()=>openEditRecord(record)}>Editar</Btn>}
      </div>
    </div>{isEditingThisRecord && <div style={{marginTop:12}}>{renderVetInlineEditor()}</div>}</React.Fragment>;
  };

  const currentList = vetView === "lot" ? filteredLotRecords : filteredIndividualRecords;
  const visibleVetList = isVetInlineFocusMode ? (form?._new ? [] : (focusedVetRecordId ? currentList.filter(record=>String(record.id)===focusedVetRecordId) : [])) : currentList;

  return <div><Toast toast={toast}/>
    {!!vetSummaryCards.length && <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:14,marginBottom:24}}>
      {vetSummaryCards.map(card=><StatCard key={card.id} icon={card.icon} label={card.label} value={card.value} color={card.color}/>) }
    </div>}

    <div style={{display:"grid",gap:14,marginBottom:14}}>
      <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
        <div>
          <div style={{fontSize:11,fontWeight:800,color:T.accent,textTransform:"uppercase",letterSpacing:.8}}>ATENDIMENTOS</div>
          <div style={{fontSize:13,color:T.muted,maxWidth:720,marginTop:4}}>Use submenus separados para localizar rapidamente atendimentos individuais e em lote, sempre com edição inline abaixo do registro.</div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {canCreate&&<Btn variant={form?._new && form?.entryMode === "individual" ? "primary" : "ghost"} onClick={()=>openNewRecord({}, "individual")}>+ Individual</Btn>}
          {canCreate&&<Btn variant={form?._new && form?.entryMode === "lot" ? "primary" : "ghost"} onClick={()=>openNewRecord({}, "lot")}>+ Lote</Btn>}
        </div>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <Btn size="sm" variant={vetView === "individual" ? "primary" : "ghost"} onClick={()=>setVetView("individual")}>Individuais</Btn>
        <Btn size="sm" variant={vetView === "lot" ? "primary" : "ghost"} onClick={()=>setVetView("lot")}>Lotes</Btn>
        <Btn size="sm" variant={vetView === "agenda" ? "primary" : "ghost"} onClick={()=>setVetView("agenda")}>Agenda</Btn>
      </div>
    </div>

    {form && form._new && vetView !== "agenda" && <div ref={vetInlineTopRef}>{renderVetInlineEditor({ marginBottom:18 })}</div>}

    {vetView === "agenda" ? <>
      {!!vetAgendaCards.length && <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:14,marginBottom:18}}>{vetAgendaCards.map(card=><StatCard key={card.id} icon={card.icon} label={card.label} value={card.value} color={card.color} />)}</div>}
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:20,padding:isCompactViewport()?14:18,marginBottom:16}}>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"minmax(180px,220px) auto minmax(220px,1fr)",gap:10,marginBottom:16,alignItems:"center"}}>
          <div style={{display:"flex",gap:4,background:T.card2,borderRadius:8,padding:2,flexWrap:"wrap"}}>{[{id:"month",label:"🗓️ Mês"},{id:"week",label:"📅 Semana"},{id:"day",label:"📋 Dia"}].map(m=><div key={m.id} onClick={()=>setVetAgendaView(m.id)} style={{padding:"6px 12px",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:600,background:vetAgendaView===m.id?(T.accent||T.blue):"transparent",color:vetAgendaView===m.id?"#fff":T.muted}}>{m.label}</div>)}</div>
          <div style={{display:"flex",alignItems:"center",justifyContent:isCompactViewport()?"space-between":"center",gap:8,flexWrap:"wrap"}}><Btn variant="ghost" onClick={()=>{ if(vetAgendaView==="month") setVetAgendaMonth(d=>new Date(d.getFullYear(), d.getMonth()-1, 1)); else if(vetAgendaView==="week") setVetAgendaSelectedDate(toDateInputValue(addDays(new Date(`${selectedAgendaDate}T12:00:00`), -7))); else setVetAgendaSelectedDate(toDateInputValue(addDays(new Date(`${selectedAgendaDate}T12:00:00`), -1))); }}>←</Btn><div style={{fontSize:vetAgendaView==="month"?18:13,fontWeight:800,color:T.text,textTransform:vetAgendaView==="month"?"capitalize":"none",textAlign:"center",flex:1}}>{vetAgendaView==="month" ? monthTitle(vetAgendaMonth) : vetAgendaView==="week" ? `${fmtDate(toDateInputValue(addDays(new Date(`${selectedAgendaDate}T12:00:00`), -((new Date(`${selectedAgendaDate}T12:00:00`).getDay()+6)%7))))} - ${fmtDate(toDateInputValue(addDays(addDays(new Date(`${selectedAgendaDate}T12:00:00`), -((new Date(`${selectedAgendaDate}T12:00:00`).getDay()+6)%7)), 6)))}` : fmtDate(selectedAgendaDate)}</div><Btn variant="ghost" onClick={()=>{ if(vetAgendaView==="month") setVetAgendaMonth(d=>new Date(d.getFullYear(), d.getMonth()+1, 1)); else if(vetAgendaView==="week") setVetAgendaSelectedDate(toDateInputValue(addDays(new Date(`${selectedAgendaDate}T12:00:00`), 7))); else setVetAgendaSelectedDate(toDateInputValue(addDays(new Date(`${selectedAgendaDate}T12:00:00`), 1))); }}>→</Btn></div>
          <div style={{fontSize:12,color:T.muted}}>Agenda por dia, semana ou mês.</div>
        </div>
        {(()=>{ const monthStart = new Date(vetAgendaMonth.getFullYear(), vetAgendaMonth.getMonth(), 1); const monthStartWeekday = (monthStart.getDay()+6)%7; const gridStart = addDays(monthStart, -monthStartWeekday); const calendarDays = Array.from({length:42}, (_, idx)=>addDays(gridStart, idx)); const selectedObj = new Date(`${selectedAgendaDate}T12:00:00`); const weekStart = addDays(selectedObj, -((selectedObj.getDay()+6)%7)); const weekDates = Array.from({length:7}, (_, idx)=>addDays(weekStart, idx)); const weekGroups = weekDates.map(day=>({ dateKey: toDateInputValue(day), date: day, items: vetAgendaMarkerMap[toDateInputValue(day)] || [] })); return <>{vetAgendaView==="month" && <><div style={{display:"grid",gridTemplateColumns:"repeat(7,minmax(0,1fr))",gap:8,marginBottom:8}}>{weekDays.map(day=><div key={day} style={{fontSize:12,fontWeight:700,color:T.muted,textAlign:"center",paddingBottom:4}}>{day}</div>)}</div><div style={{display:"grid",gridTemplateColumns:"repeat(7,minmax(0,1fr))",gap:8}}>{calendarDays.map(day=>{ const dateKey = toDateInputValue(day); const dayMarkers = vetAgendaMarkerMap[dateKey] || []; const dots = Array.from(new Map(dayMarkers.map(marker=>[marker.key, marker])).values()); const inMonth = day.getMonth() === vetAgendaMonth.getMonth(); const isSelected = selectedAgendaDate === dateKey; return <button type="button" key={dateKey} onClick={()=>{setVetAgendaSelectedDate(dateKey); setVetAgendaView("day");}} style={{minHeight:isCompactViewport()?70:84,background:T.card2,border:`1px solid ${isSelected ? (T.accent||T.blue) : T.border}`,borderRadius:18,padding:"10px 8px",display:"flex",flexDirection:"column",justifyContent:"space-between",alignItems:"flex-start",opacity:inMonth?1:.5,boxShadow:isSelected?`0 0 0 1px ${T.accent||T.blue}55 inset`:"none",cursor:"pointer"}}><span style={{fontSize:16,fontWeight:800,color:T.text}}>{day.getDate()}</span><span style={{display:"flex",gap:6,flexWrap:"wrap"}}>{dots.slice(0,4).map(marker=><span key={marker.key} title={marker.label} style={{width:12,height:12,borderRadius:999,background:marker.color,display:"inline-block",boxShadow:`0 0 0 2px ${T.card2}`}} />)}</span></button>; })}</div></>}{vetAgendaView==="week" && <div style={{display:"grid",gridTemplateColumns:"repeat(7,minmax(0,1fr))",gap:8}}>{weekGroups.map(group=>{ const dots = Array.from(new Map(group.items.map(marker=>[marker.key, marker])).values()); const isSelected = selectedAgendaDate===group.dateKey; return <button type="button" key={group.dateKey} onClick={()=>setVetAgendaSelectedDate(group.dateKey)} style={{minHeight:isCompactViewport()?76:92,background:T.card2,border:`1px solid ${isSelected ? (T.accent||T.blue) : T.border}`,borderRadius:16,padding:"10px 8px",display:"flex",flexDirection:"column",justifyContent:"space-between",alignItems:"flex-start",cursor:"pointer"}}><div><div style={{fontSize:11,fontWeight:700,color:T.muted}}>{weekDays[(group.date.getDay()+6)%7]}</div><div style={{fontSize:16,fontWeight:800,color:T.text,marginTop:4}}>{group.date.getDate()}</div></div><span style={{display:"flex",gap:6,flexWrap:"wrap"}}>{dots.slice(0,4).map(marker=><span key={marker.key} style={{width:12,height:12,borderRadius:999,background:marker.color,display:"inline-block"}} />)}</span></button>; })}</div>}<div style={{display:"flex",gap:14,flexWrap:"wrap",marginTop:16,fontSize:12,color:T.muted}}><span style={{display:"inline-flex",alignItems:"center",gap:6}}><span style={{width:12,height:12,borderRadius:999,background:T.blue||T.accent,display:"inline-block"}} /> Agendados</span><span style={{display:"inline-flex",alignItems:"center",gap:6}}><span style={{width:12,height:12,borderRadius:999,background:T.green,display:"inline-block"}} /> Realizados</span><span style={{display:"inline-flex",alignItems:"center",gap:6}}><span style={{width:12,height:12,borderRadius:999,background:T.red,display:"inline-block"}} /> Não realizados</span><span style={{display:"inline-flex",alignItems:"center",gap:6}}><span style={{width:12,height:12,borderRadius:999,background:T.orange||T.yellow,display:"inline-block"}} /> Em andamento</span></div>{vetAgendaView==="week" ? <div style={{display:"flex",flexDirection:"column",gap:12,marginTop:16}}>{weekGroups.map(group=><div key={`week-group-${group.dateKey}`}>{renderVetAgendaItems(group.items, fmtDate(group.dateKey), "Nenhum atendimento nesse dia da semana.")}</div>)}</div> : <div style={{marginTop:16}}>{renderVetAgendaItems(vetAgendaSelectedItems, fmtDate(selectedAgendaDate), "Nenhum atendimento nesse dia.")}</div>}</>; })()}
      </div>
    </> : <>
      {!isVetInlineFocusMode && <div style={{display:"grid",gap:12,marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap",alignItems:"center"}}>
          <div>
            <div style={{fontSize:16,fontWeight:800,color:T.text}}>{vetView === "lot" ? "Atendimentos · Lotes" : "Atendimentos · Individuais"}</div>
            <div style={{fontSize:12,color:T.muted,marginTop:2}}>{vetView === "lot" ? "Filtre e edite os registros de lotes abaixo do item correspondente." : "Filtre e edite os registros individuais abaixo do item correspondente."}</div>
          </div>
          <div style={{minWidth:isCompactViewport()?"100%":320,flex:isCompactViewport()?"1 1 100%":"0 0 320px"}}>
            <Input placeholder={vetView === "lot" ? "Filtrar por lote, localização, tipo ou responsável" : "Filtrar por animal, brinco, tipo ou responsável"} value={vetView === "lot" ? lotSearch : individualSearch} onChange={e=>vetView === "lot" ? setLotSearch(e.target.value) : setIndividualSearch(e.target.value)} />
          </div>
        </div>
      </div>}

      <div style={{display:"grid",gridTemplateColumns:cardGrid,gap:12}}>
        {visibleVetList.map(renderRecordCard)}
        {!isVetInlineFocusMode && !currentList.length && <div style={{gridColumn:"1/-1",color:T.muted,padding:32,textAlign:"center",border:`1px dashed ${T.border}`,borderRadius:16}}>{vetView === "lot" ? "Nenhum atendimento em lote registrado ainda." : "Nenhum atendimento veterinário individual registrado ainda."}</div>}
      </div>
    </>}

  </div>;
}


  return VetModule;
}
